<template>
    <div :class="['page page--gradient-background', { 'page--embed': partnerAppEmbedCustomization }]">
        <section class="row row--kyc">
            <div v-if="!isPartnerAPI"
                class="wrapper wrapper--max-width-dynamic"
            >
                <header :class="['row__header', { 'row__header--partner-app': isPartnerApp }]">
                    <back-button
                        :isAbsolute="false"
                        :isBlack="true"
                        :isHiddenOnDesktop="true"
                        :customCallback="routeToHome"
                    />
                    <h1>{{ $t('profile.personalVerification') }}</h1>
                </header>
                <div class="kyc-tiles">
                    <carousel class="VueCarousel--paginate-mobile-only"
                        :paginationEnabled="true"
                        paginationColor="#fff"
                        paginationActiveColor="#8EC641"
                        :navigationEnabled="true"
                        navigation-next-label=""
                        navigation-prev-label=""
                        :scrollPerPage="false"
                        :perPageCustom="[
                            [320, 1],
                            [400, 1.2],
                            [450, 1.4],
                            [525, 1.6],
                            [600, 1.8],
                            [700, 2.1],
                            [768, 1.7],
                            [900, 2],
                            [1000, 2.5],
                            [1150, 3],
                            [1300, 3.5],
                            [1500, 4],
                        ]"
                    >
                        <slide v-for="(level, index) in levels"
                            :key="index"
                        >
                            <div>
                                <icon-tile :url="level.url"
                                    :level="level"
                                    :active="isActive(index + 1)"
                                    :disabled="isDisabled(index + 1)"
                                    :no-hover="true"
                                    :icon="`level-${level.name}`"
                                    :status="getStatus(index + 1)"
                                    :actionTitle="$t('kycUserIdentity.limitsAndAvailableFeatures')"
                                    :slotTitle="getText(index + 1, getStatus(index + 1))"
                                    @actionClick="appendLevelModal(index + 1)"
                                >
                                    <div class="title-wrapper">
                                        <span class="title">{{ $t(`profile.kyc.level${index + 1}Text`) }}</span>
                                        <span>{{ getText(index + 1, getStatus(index + 1)) }}</span>
                                    </div>
                                </icon-tile>
                            </div>
                        </slide>
                    </carousel>
                </div>
            </div>
        </section>
        <section class="row content">
            <template v-if="showLevelsForm">
                <kyc-email v-if="level === 1" />

                <kyc-level-two v-else-if="level === 2"
                    @appendModal="appendImageModal"
                />

                <kyc-level-three v-else-if="level === 3"
                    @appendModal="appendImageModal"
                    v-on="$listeners"
                />

                <kyc-level-four v-else-if="level === 4" />
            </template>

            <div v-else
                class="content__kyc-successful"
            >
                <h1>{{ $t('profile.kyc.validationComplete') }}</h1>
                <img src="@webapp/assets/kyc-successful.webp"
                    alt="KYC Successful"
                >
            </div>
        </section>
    </div>
</template>

<script>
// 3rd party
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import kycEmail from '@webapp/components/kyc/kyc-email.vue';
import kycLevelTwo from '@webapp/components/kyc/level-two/kyc-level-two.vue';
import kycLevelThree from '@webapp/components/kyc/kyc-level-three.vue';
import kycLevelFour from '@webapp/components/kyc/kyc-level-four.vue';
import iconTile from '@webapp/components/tiles/icon-tile.vue';

import {
    Carousel,
    Slide,
} from 'vue-carousel';

// Local Types
import localEpicTypes from '@webapp/store/types.js';

// Types
import {
    KycStatus,
} from '@webapp/types/kyc';

export default {
    name: 'kyc',
    components: {
        backButton,
        kycEmail,
        kycLevelTwo,
        kycLevelThree,
        kycLevelFour,
        iconTile,
        Carousel,
        Slide,
    },
    props: {
        level: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            levels: [
                {
                    name: 'one',
                    url: '/kyc',
                },
                {
                    name: 'two',
                    url: '/kyc/level-2',
                },
                {
                    name: 'three',
                    url: '/kyc/level-3',
                },
                {
                    name: 'four',
                    url: '/kyc/level-4',
                },
            ],
            image: '',
            showImagePreviewModal: false,
            showLevelModal: false,
            modalLevel: 1,
            maximumLevel: 4,
        };
    },
    computed: {
        ...mapGetters([
            'userDetails',
            'kycTierLevel',
            'kycTierLevels',
            'reverifyKyc',
            'isPartnerApp',
            'partnerAppData',
            'userId',
            'kycLevel2ExpirationStatus',
            'partnerAppEmbedCustomization',
        ]),
        ...mapGetters('turkishRequirements', ['isTurkishQuestionaryAccepted', 'isAppBlockedByTurkishRequirements']),

        isFullyVerified() {
            return this.kycTierLevel === this.maximumLevel;
        },

        shouldRedirectToKycLevel() {
            return this.kycTierLevel > 0 && (!this.isFullyVerified || this.kycLevel2ExpirationStatus) && !this.reverifyKyc;
        },

        routeToHome() {
            return {
                path: '/home/overview',
            };
        },

        isPartnerAPI() {
            return Boolean(new URLSearchParams(location.search).get('origin_partner_api') || '');
        },

        showLevelsForm() {
            return !this.isFullyVerified || this.reverifyKyc || this.kycLevel2ExpirationStatus;
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler({ path: toPath }) {
                if (this.kycTierLevel > 0 &&
                this.isAppBlockedByTurkishRequirements) {

                    return this.$router.push(`${this.levels[1].url}`).catch(() => false);
                }

                if (toPath?.includes?.('level') &&
                    this.levels[this.kycTierLevel] &&
                    toPath !== this.levels[this.kycTierLevel].url) {
                    if (toPath === this.levels[1].url && this.kycLevel2ExpirationStatus) {
                        return;
                    }

                    return this.$router.replace(this.levels[this.kycTierLevel].url);
                }

                if (toPath === '/kyc' && this.shouldRedirectToKycLevel) {
                    let levelToRouteTo = this.kycTierLevel;

                    if (this.kycLevel2ExpirationStatus) {
                        return this.$router.push(`${this.levels[1].url}`).catch(() => false);
                    }

                    // This is mainly in case of level 4
                    // If the last level is approved, the routing will break because we try to route to the next level that can be filled in
                    if (this.kycTierLevel > this.levels.lastIndex) {
                        levelToRouteTo--;
                    }

                    this.$router.push(`${this.levels[levelToRouteTo].url}`).catch(() => false);
                }
            },
        },
    },
    methods: {
        ...mapActions({
            setModal: localEpicTypes.SET_MODAL,
        }),
        isActive(kycLevel) {
            return this.level === kycLevel;
        },
        isDisabled(level) {
            return (((level > 2 && this.isAppBlockedByTurkishRequirements)) || this.isLevelNotAvailable(level) || this.isLevelValidated(level));

        },
        // Checking whether the level can be verified. If we are on level 1, level 2 is active and level 3 is not available yet
        isLevelNotAvailable(level) {
            return (level > this.kycTierLevel + 1) || (level > this.maximumLevel);
        },
        isLevelValidated(level) {
            return this.getStatus(level) === KycStatus.Validated;
        },
        getStatus(level) {
            const status = KycStatus.Empty;
            const tierLevel = this.kycTierLevels.find((tierLevel) => tierLevel.tierLevel === level);

            if (level === 2 && this.isAppBlockedByTurkishRequirements) {
                if (this.isTurkishQuestionaryAccepted) {
                    return this.kycLevel2ExpirationStatus || tierLevel?.status || status;
                }

                return status;
            }

            if (this.kycTierLevel >= 0 && this.kycTierLevels) {
                if (tierLevel?.tierLevel === 2 && this.kycLevel2ExpirationStatus) {
                    return this.kycLevel2ExpirationStatus;
                } else if (tierLevel?.status) {
                    return tierLevel.status;
                }
            }

            return status;
        },
        getText(index, status) {
            let text = this.$t(`profile.kyc.level${index}BelowText`);

            if (index === 2 && this.kycLevel2ExpirationStatus) {
                return this.$t(`kycLevel2Tile.${this.kycLevel2ExpirationStatus}.description`);
            }

            if (status === KycStatus.Processing) {
                if (this.kycTierLevel === 0) {
                    text = `${this.$t('kycStatusVerification.checkInbox')}`;
                } else {
                    text = `${this.$t('kycLevel2VerificationStatus.title')}`;
                }
            }

            return text;
        },
        appendLevelModal(modalLevel) {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/kyc/kyc-modal',
                data: {
                    modalLevel,
                },
            });
        },
        appendImageModal(image) {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/kyc/kyc-image-preview-modal',
                data: {
                    image,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.row--kyc {
    padding-bottom: 6.5rem;

    @include max-width(sm) {
        padding-bottom: 1.5rem;
    }
}

.content {
    min-height: calc(100vh - 35rem);

    @include max-width(sm) {
        min-height: calc(100vh - 41.5rem);
    }

    &__kyc-successful {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;
        margin-bottom: 12rem;

        h1 {
            font-size: 2.6rem;
            line-height: 4rem;
            color: $cyprus-color;
            width: 90%;
            text-align: center;

            @include min-width(lg) {
                font-size: 3.6rem;
                width: 45%;
            }
        }

        img {
            object-fit: contain;
            width: 90%;

            @include min-width(md) {
                width: unset;
                object-fit: unset;
            }
        }
    }
}

::v-deep {
    .icons-tile__top {
        min-height: 14rem;
    }
}

.title-wrapper {
    .title {
        font-size: 1.5rem;
        line-height: 2.5rem;
        font-weight: 700;

        &--yellow {
            color: $buttercup-color;
        }
    }

    span {
        font-size: 1.3rem;
        line-height: 1.8rem;
        font-weight: 400;
        @include line-clamp(3);
    }
}
</style>